import { logger } from '@utils';
import { delay, SagaIterator } from 'redux-saga';
import { call, race, select, take } from 'redux-saga/effects';
import { IChat } from '@app-types/websocket/chat.types';
import { websocketAuthenticated, websocketDisconnected } from '@services/common/common.action';
import { commonWebSocket } from '@services/common/common.constants';
import { findNewestHistoryGap, IChatHistoryWindow, missingIdsInWindow } from './chatHistoryGaps';
import * as ChatWebsocket from './chat.websocket';

const HISTORY_GAP_REQUEST_PREFIX = 'CHAT_HISTORY_GAP:';
const HISTORY_GAP_RESPONSE_TIMEOUT_MS = 20000;
// Up to 1,000 messages per chat and app session. Scrolling loads anything older.
export const HISTORY_GAP_MAX_REQUESTS_PER_CHAT = 20;

interface IHistoryGapBookkeeping {
  // Ids the server answered without. Requesting them again would loop forever.
  unavailableIds: Map<number, Set<number>>;
  // Answered requests per chat.
  requestCounts: Map<number, number>;
  // Chats whose request failed or got no answer wait for the next connection.
  skippedChatIds: Set<number>;
}

interface IHistoryGap {
  chatId: number;
  window: IChatHistoryWindow;
}

const HISTORY_GAP_TRIGGERS = [
  commonWebSocket.snapshot.CHATS_SNAPSHOT,
  commonWebSocket.special.CHAT_UPDATE,
  commonWebSocket.special.NEW_CHAT,
  websocketAuthenticated.getType(),
];

function isHistoryGapTrigger(action: any): boolean {
  return !!action && HISTORY_GAP_TRIGGERS.indexOf(action.type) !== -1;
}

function isConnected(state: any): boolean {
  return !!state?.connection?.authenticated || !!state?.connection?.loadCompleted;
}

function getChats(state: any): IChat[] {
  return Array.isArray(state?.chat?.chats) ? state.chat.chats : [];
}

function findNextHistoryGap(chats: IChat[], bookkeeping: IHistoryGapBookkeeping): IHistoryGap | undefined {
  for (const chat of chats) {
    if (!chat || !Number.isInteger(chat.id) || chat.id <= 0
      || bookkeeping.skippedChatIds.has(chat.id)
      || (bookkeeping.requestCounts.get(chat.id) || 0) >= HISTORY_GAP_MAX_REQUESTS_PER_CHAT) {
      continue;
    }
    const window = findNewestHistoryGap(chat.messages, bookkeeping.unavailableIds.get(chat.id));
    if (window) {
      return {chatId: chat.id, window};
    }
  }
  return undefined;
}

/**
 * The reconnect snapshot only carries the newest message of each chat, so
 * messages sent while the socket was down leave a hole in the cached history.
 * Fill the holes one request at a time, newest first.
 */
function* fillChatHistoryGaps(bookkeeping: IHistoryGapBookkeeping): SagaIterator {
  while (true) {
    const state: any = yield select();
    if (!isConnected(state)) {
      return;
    }
    const gap = findNextHistoryGap(getChats(state), bookkeeping);
    if (!gap) {
      return;
    }

    const {chatId, window} = gap;
    const requestId = `${HISTORY_GAP_REQUEST_PREFIX}${chatId}:${window.startId}:${Date.now()}`;
    yield call(ChatWebsocket.requestChatMessages, {chatId, ...window}, requestId);

    const result: any = yield race({
      loaded: take((action: any) => action.type === commonWebSocket.special.CHAT_UPDATE
        && action.payload?.requestId === requestId),
      failed: take((action: any) => action.type === commonWebSocket.special.REQUEST_ID
        && action.payload?.requestId === requestId && action.payload.hasError),
      disconnected: take(websocketDisconnected),
      timeout: call(delay, HISTORY_GAP_RESPONSE_TIMEOUT_MS),
    });

    if (result.loaded) {
      bookkeeping.requestCounts.set(chatId, (bookkeeping.requestCounts.get(chatId) || 0) + 1);
      // The reply holds every message the server has in the window, so the
      // ids still missing do not exist or are not visible to this user.
      const repliedState: any = yield select();
      const chat = getChats(repliedState).find((c) => !!c && c.id === chatId);
      const unavailable = bookkeeping.unavailableIds.get(chatId) || new Set<number>();
      missingIdsInWindow(chat ? chat.messages : [], window).forEach((id) => unavailable.add(id));
      bookkeeping.unavailableIds.set(chatId, unavailable);
    } else if (result.disconnected) {
      return;
    } else {
      logger.info(`Chat history request ${requestId} ${result.failed ? 'failed' : 'timed out'}`);
      bookkeeping.skippedChatIds.add(chatId);
    }
  }
}

export function* watchChatHistoryGaps(): SagaIterator {
  const bookkeeping: IHistoryGapBookkeeping = {
    unavailableIds: new Map<number, Set<number>>(),
    requestCounts: new Map<number, number>(),
    skippedChatIds: new Set<number>(),
  };
  while (true) {
    const trigger: any = yield take(isHistoryGapTrigger);
    if (trigger.type === websocketAuthenticated.getType()) {
      bookkeeping.skippedChatIds.clear();
    }
    try {
      yield call(fillChatHistoryGaps, bookkeeping);
    } catch (error) {
      logger.info(error);
    }
  }
}

export function* createChat(action: any): SagaIterator {
  try {
    ChatWebsocket.createChat(action.payload);
  } catch (error) {
    logger.info(error);
  }
} 

export function* createChatMessage(action: any): SagaIterator {
  try {
    ChatWebsocket.createChatMessage(action.payload);
  } catch (error) {
    logger.info(error);
  }
} 

export function* saveChat(action: any): SagaIterator {
  try {
    ChatWebsocket.saveChat(action.payload);
  } catch (error) {
    logger.info(error);
  }
} 

export function* createChatMessageToxicChecked(action: any): SagaIterator {
  try {
    ChatWebsocket.createChatMessageToxicChecked(action.payload);
  } catch (error) {
    logger.info(error);
  }
} 

export function* createChatMessageToxicCheckedAction(action: any): SagaIterator {
  try {
    ChatWebsocket.createChatMessageToxicCheckedAction(action.payload);
  } catch (error) {
    logger.info(error);
  }
} 

export function* chatCheckIn(action: any): SagaIterator {
  try {
    ChatWebsocket.chatCheckIn(action.payload);
  } catch (error) {
    logger.info(error);
  }
}

export function* toxicCheck(action: any): SagaIterator {
  try {
    ChatWebsocket.toxicCheck(action.payload);
  } catch (error) {
    logger.info(error);
  }
} 

export function* inviteMember(action: any): SagaIterator {
  try {
    ChatWebsocket.inviteMember(action.payload);
  } catch (error) {
    logger.info(error);
  }
}

export function* requestChatMessages(action: any): SagaIterator {
  try {
    ChatWebsocket.requestChatMessages(action.payload);
  } catch (error) {
    logger.info(error);
  }
}


export function* sendChatReceipt(action: any): SagaIterator {
  try {
    ChatWebsocket.chatReadReceipt(action.payload.chatId);
    
  } catch (error) {
    logger.info(error);
  }
}

export function* requestMessageReadInfo(action: any): SagaIterator {
  try {
    ChatWebsocket.requestMessageReadInfo(action.payload);
  } catch (error) {
    logger.info(error);
  }
}

export function* archiveChat(action: any): SagaIterator {
  try {
    ChatWebsocket.archiveChat(action.payload);
    
  } catch (error) {
    logger.info(error);
  }
}

export function* renameChat(action: any): SagaIterator {
  try {
    ChatWebsocket.renameChat(action.payload);
    
  } catch (error) {
    logger.info(error);
  }
}



// WEBPACK FOOTER //
// ./src/services/chat/chat.saga.ts